/**
 * Read-aloud: the one speaker for the whole page, and the button that asks it to speak.
 *
 * Tap to hear, never automatic — nothing on a page starts talking by itself. One thing speaks at a
 * time: tapping the button that is speaking stops it, tapping another switches to that, and leaving
 * the page stops it.
 *
 * Buttons are created everywhere straight away but stay invisible until a voice is known to exist
 * (`:root[data-read-aloud]` in style.css). Voices load asynchronously, and some devices have no
 * on-device English voice at all — they get no buttons, never a button that does nothing. The
 * choice of voice and the splitting of text live in say.ts, where they are tested.
 */

import { el } from "../dom.js";
import { track } from "../telemetry.js";
import { pickVoice, sentences } from "./say.js";

/** A little slower than a browser's default: the listener is a child, often a young one. */
const RATE = 0.9;
const READ_LABEL = "Read aloud";
const STOP_LABEL = "Stop reading";

let started = false;
let voice: SpeechSynthesisVoice | null = null;
/** The button whose text is being spoken, and a token that goes stale when it is stopped. */
let current: { button: HTMLButtonElement; token: number } | null = null;
let nextToken = 0;

function synth(): SpeechSynthesis | null {
  return typeof window !== "undefined" && "speechSynthesis" in window ? window.speechSynthesis : null;
}

function start(): void {
  if (started) return;
  started = true;
  const s = synth();
  if (!s) return;
  const choose = (): void => {
    voice = pickVoice(s.getVoices());
    if (voice) document.documentElement.dataset["readAloud"] = "ready";
    else delete document.documentElement.dataset["readAloud"];
  };
  choose();
  s.addEventListener("voiceschanged", choose);
  // A page being left must not keep talking over the next one.
  addEventListener("pagehide", () => stop());
}

function setPlaying(button: HTMLButtonElement, playing: boolean): void {
  button.setAttribute("aria-pressed", playing ? "true" : "false");
  button.setAttribute("aria-label", playing ? STOP_LABEL : READ_LABEL);
  button.title = playing ? STOP_LABEL : READ_LABEL;
}

export function stop(): void {
  const s = synth();
  if (current) setPlaying(current.button, false);
  current = null;
  s?.cancel();
}

function speak(button: HTMLButtonElement, text: string): void {
  const s = synth();
  if (!s || !voice) return;
  const pieces = sentences(text);
  if (pieces.length === 0) return;

  stop();
  const token = ++nextToken;
  current = { button, token };
  setPlaying(button, true);
  const chosen = voice;
  const finish = (): void => {
    if (current?.token !== token) return;
    setPlaying(button, false);
    current = null;
  };

  pieces.forEach((piece, index) => {
    const utterance = new SpeechSynthesisUtterance(piece);
    utterance.voice = chosen;
    utterance.lang = chosen.lang;
    utterance.rate = RATE;
    if (index === pieces.length - 1) utterance.onend = finish;
    // Any piece failing ends the whole reading; "interrupted"/"canceled" is our own stop().
    utterance.onerror = finish;
    s.speak(utterance);
  });
}

/**
 * A speaker button that reads `getText()` aloud. The text is read when tapped, not when the button
 * is made, so it follows the page as it is then — options in their on-screen order, a list after
 * she has reordered it, feedback that has appeared since.
 */
export function speakerButton(
  getText: () => string,
  telemetry: { blockId?: string; what: string },
): HTMLButtonElement {
  start();
  const button = el("button", {
    class: "speak-btn",
    attrs: { type: "button", "aria-pressed": "false", "aria-label": READ_LABEL, title: READ_LABEL },
  });
  button.appendChild(el("span", { class: "speak-icon", text: "🔊", attrs: { "aria-hidden": "true" } }));
  button.addEventListener("click", (event) => {
    // A speaker inside a pill row or a card must not also trigger what it sits in.
    event.preventDefault();
    event.stopPropagation();
    if (current?.button === button) {
      stop();
      return;
    }
    const text = getText().trim();
    if (!text) return;
    track("interact", {
      action: "read-aloud",
      value: telemetry.what,
      ...(telemetry.blockId === undefined ? {} : { blockId: telemetry.blockId }),
    });
    speak(button, text);
  });
  return button;
}

/** Puts a speaker at the top of a lesson block (floated right by style.css). */
export function addSpeaker(shell: HTMLElement, getText: () => string, blockId?: string): void {
  const button = speakerButton(getText, {
    what: shell.className.replace(/^block block-/, "").split(" ")[0] ?? "block",
    ...(blockId === undefined ? {} : { blockId }),
  });
  shell.insertBefore(button, shell.firstChild);
}
